/**
 * Which phase of the innings is being bowled: powerplay, middle, or death.
 *
 * ## Why this is derived rather than read
 *
 * #42 asks for the badge "based on current over vs powerplay/death overs config from
 * RuleSet". **`RuleSet` has no such config.** It carries `overs`, `ballsPerOver`,
 * `maxOversPerBowler` and the law switches, and nothing about field restrictions — see
 * `packages/engine/src/ruleset.ts`. Adding fields to it is the engine lane's (#39), not this
 * one's, and inventing them here would put a second definition of the format in the client.
 *
 * So the thresholds are proportional, and chosen so the format that matters lands exactly on
 * the convention: **first 30% is the powerplay, last 20% is the death.** For a T20 that is
 * overs 1–6 and overs 17–20, which is what a scorer means by the words. For a five-over box
 * game it is over 1–2 and the last one, which is at least defensible.
 *
 * It is *not* right for a 50-over ODI, where the mandatory powerplay is the first 10 rather
 * than the first 15. When #39 puts real powerplay overs on the RuleSet, this function reads
 * them and the constants below go — the seam is here so that change is a one-file edit with
 * a test already around it.
 *
 * A timed format has no phases at all, which is why this can return null: "Death" over a
 * match with no over limit is a statement about an innings that has no end in sight.
 */

export type InningsPhase = "powerplay" | "middle" | "death";

/** First 30% of the innings. T20: overs 1–6. */
const POWERPLAY_SHARE = 0.3;
/** Last 20% of the innings. T20: overs 17–20. */
const DEATH_SHARE = 0.2;

/**
 * The phase of the over currently being bowled.
 *
 * `completedOvers` is `MatchState.header.overs.completed` — the count of overs *finished*, so
 * the over in progress is `completedOvers + 1` in the way a human counts them.
 *
 * Null when the format has no over limit, and null once the innings has run past its
 * allocation, which happens for a moment at the end of the last over before the innings
 * closes — labelling that "Death" is harmless, but labelling nothing is honest.
 */
export function inningsPhase(
  completedOvers: number,
  totalOvers: number | null,
): InningsPhase | null {
  if (totalOvers === null || totalOvers <= 0) return null;
  if (completedOvers < 0 || completedOvers >= totalOvers) return null;

  // At least one over each, so a two-over game still has a powerplay and a death rather
  // than rounding both to zero and calling the whole thing the middle.
  const powerplayOvers = Math.max(1, Math.ceil(totalOvers * POWERPLAY_SHARE));
  const deathOvers = Math.max(1, Math.ceil(totalOvers * DEATH_SHARE));

  if (completedOvers < powerplayOvers) return "powerplay";
  // A very short format can have the two windows overlap — three overs gives a powerplay of
  // one and a death of one, leaving one over in the middle, but two overs gives powerplay 1
  // and death 1 with nothing between. The powerplay is checked first, so the opening over
  // keeps its name and there is simply no middle. That is the right answer for a game that
  // short.
  if (completedOvers >= totalOvers - deathOvers) return "death";
  return "middle";
}

/** What the badge says. Capitalised as a label, not as prose. */
export const phaseLabel = (phase: InningsPhase): string =>
  phase === "powerplay" ? "Powerplay" : phase === "death" ? "Death" : "Middle";
